import React, { useEffect } from 'react'
import ListPagination from 'Components/ListPagination/ListPagination'
import { useListPagination } from 'Hooks/useListPagination'
import './VouchersList.scss'
import './VoucherDetailsModal.scss'

import filterIcon from 'BoostXpressAdmin/Assets/Icon/Vouchers/filter.svg'
import exportIcon from 'BoostXpressAdmin/Assets/Icon/Vouchers/export.svg'
// import arrowRight from 'BoostXpressAdmin/Assets/Icon/Vouchers/arrow-right.svg'
import { VoucherXModel } from 'BoostXpressAdmin/Model/voucherXModel'
import { voucherBXService } from 'BoostXpressAdmin/Services/voucherBXService'
import { HorizontalLoader, ModalWrapper } from 'Components'

const VouchersList = () => {
  const [vouchers, setVouchers] = React.useState<VoucherXModel[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = React.useState(false)
  const [selectedVoucher, setSelectedVoucher] = React.useState<VoucherXModel | null>(null)
  const {
    page: currentPage,
    setPage: setCurrentPage,
    limit: itemsPerPage,
    changePageSize,
  } = useListPagination()
  const [totalItems, setTotalItems] = React.useState(0)

  // Search and filter states
  const [searchTerm, setSearchTerm] = React.useState('')
  const [showFilters, setShowFilters] = React.useState(false)
  const [status, setStatus] = React.useState('')
  const [paymentStatus, setPaymentStatus] = React.useState('')
  const [startDate, setStartDate] = React.useState('')
  const [endDate, setEndDate] = React.useState('')

  useEffect(() => {
    let cancelled = false
    const loadVouchers = async () => {
      try {
        setIsLoading(true)

        // Build filter parameters
        const params: any = {
          page: currentPage,
          limit: itemsPerPage,
          sortOrder: 'DESC',
        }

        // Add search if exists
        if (searchTerm) {
          // Check if it's a userResource ID (numeric)
          const isNumeric = /^\d+$/.test(searchTerm.trim())
          if (isNumeric) {
            params.userResourceId = searchTerm.trim()
          }
        }

        // Add filters
        if (status) params.status = status
        if (paymentStatus) params.paymentStatus = paymentStatus
        if (startDate) params['startDateYYYY_MM_DD'] = startDate
        if (endDate) params['endDateYYYY_MM_DD'] = endDate

        const res = await voucherBXService.index(params)
        if (cancelled) return
        setVouchers(res.data.data ?? [])

        // Set pagination metadata
        const meta = res.data.meta?.pagination ?? res.data.meta
        setTotalItems(meta?.total ?? 0)
      } catch (e) {
        if (!cancelled) {
          setVouchers([])
          setTotalItems(0)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    loadVouchers()
    return () => {
      cancelled = true
    }
  }, [currentPage, itemsPerPage, searchTerm, status, paymentStatus, startDate, endDate])

  return (
    <div className='bx-vouchers'>
      <h2 className='bx-vouchers__title'>Vouchers Management</h2>

      <div className='bx-vouchers__toolbar'>
        <div className='bx-vouchers-search'>
          <div className='bx-vouchers-search__icon'>
            <svg
              width='17'
              height='17'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                stroke='#868484'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <input
            className='bx-vouchers-search__input'
            placeholder='Search by userResource ID'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className='bx-vouchers-actions'>
          <div className='position-relative'>
            <button
              className='bx-vouchers-actions__btn'
              onClick={() => setShowFilters(!showFilters)}
            >
              <img src={filterIcon} alt='' className='bx-vouchers-actions__icon' />
              <span className='bx-vouchers-actions__label'>Filters</span>
            </button>

            {showFilters && (
              <div className='bx-vouchers-filters-dropdown'>
                <div className='bx-filters-header'>
                  <h5>Filter Vouchers</h5>
                  <button className='bx-filters-close' onClick={() => setShowFilters(false)}>
                    ×
                  </button>
                </div>

                <div className='bx-filters-body'>
                  <div className='bx-filter-group'>
                    <label>Voucher Status</label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className='form-control'
                    >
                      <option value=''>All Status</option>
                      <option value='CREATED'>Created</option>
                      <option value='EXPIRED'>Expired</option>
                      <option value='USED'>Used</option>
                    </select>
                  </div>

                  <div className='bx-filter-group'>
                    <label>Payment Status</label>
                    <select
                      value={paymentStatus}
                      onChange={(e) => setPaymentStatus(e.target.value)}
                      className='form-control'
                    >
                      <option value=''>All Status</option>
                      <option value='unpaid'>Unpaid</option>
                      <option value='paid'>Paid</option>
                      <option value='failed'>Failed</option>
                      <option value='pending'>Pending</option>
                      <option value='refunded'>Refunded</option>
                    </select>
                  </div>

                  <div className='bx-filter-group'>
                    <label>Start Date</label>
                    <input
                      type='date'
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className='form-control'
                    />
                  </div>

                  <div className='bx-filter-group'>
                    <label>End Date</label>
                    <input
                      type='date'
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className='form-control'
                    />
                  </div>
                </div>

                <div className='bx-filters-footer'>
                  <button
                    className='btn btn-secondary'
                    onClick={() => {
                      setStatus('')
                      setPaymentStatus('')
                      setStartDate('')
                      setEndDate('')
                    }}
                  >
                    Clear Filters
                  </button>
                  <button className='btn btn-primary' onClick={() => setShowFilters(false)}>
                    Apply Filters
                  </button>
                </div>
              </div>
            )}
          </div>
          <button className='bx-vouchers-actions__btn'>
            <img src={exportIcon} alt='' className='bx-vouchers-actions__icon' />
            <span className='bx-vouchers-actions__label'>Export</span>
          </button>
        </div>
      </div>

      <div className='bx-vouchers-table-container'>
        <table className='bx-vouchers-table'>
          <thead>
            <tr>
              <th>Code</th>
              <th>Owner</th>
              <th>Amount</th>
              <th>Purchase Date</th>
              <th>Status</th>
              <th>Expiration Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7}>
                  <HorizontalLoader />
                </td>
              </tr>
            ) : vouchers.length === 0 ? (
              <tr>
                <td colSpan={7}>No vouchers found</td>
              </tr>
            ) : (
              vouchers.map((v) => (
                <tr key={v.resourceId}>
                  <td>{v.code}</td>
                  <td>
                    {v.user.firstName} {v.user.lastName}
                  </td>
                  <td>₦{v.amountMajor}</td>
                  <td>{new Date(v.createdAt).toLocaleDateString()}</td>
                  <td>
                    <span className={`bx-status-badge bx-status-badge--${v.status.toLowerCase()}`}>
                      {v.status}
                    </span>
                  </td>
                  <td>{new Date(v.expiresAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      className='bx-vouchers-actions__btn- btn-sm btn btn-default'
                      onClick={() => {
                        setSelectedVoucher(v)
                        setIsDetailsModalOpen(true)
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <ListPagination
          page={currentPage}
          limit={itemsPerPage}
          total={totalItems}
          disabled={isLoading}
          onPageChange={setCurrentPage}
          onPageSizeChange={changePageSize}
        />
      </div>

      <ModalWrapper
        showModal={isDetailsModalOpen}
        closeModal={() => setIsDetailsModalOpen(false)}
        title='Voucher Details'
        size='lg'
        hideHeader={false}
      >
        <div className='voucher-details-modal'>
          {selectedVoucher && (
            <>
              {/* Voucher Code Display */}
              <div className='voucher-code-display'>{selectedVoucher.code}</div>

              <div className='row'>
                <div className='col-md-6'>
                  {/* Voucher Information Section */}
                  <div className='voucher-detail-section'>
                    <h5 className='mb-3 text-dark'>Voucher Information</h5>

                    <div className='voucher-detail-item'>
                      <label className='voucher-detail-label'>Title</label>
                      <p className='voucher-detail-value'>{selectedVoucher.title}</p>
                    </div>

                    <div className='voucher-detail-item'>
                      <label className='voucher-detail-label'>Amount</label>
                      <div className='amount-display'>
                        ₦{selectedVoucher.amountMajor.toLocaleString()}
                      </div>
                    </div>

                    <div className='voucher-detail-item'>
                      <label className='voucher-detail-label'>Status & Payment</label>
                      <div className='status-badges'>
                        <span
                          className={`bx-status-badge bx-status-badge--${selectedVoucher.status.toLowerCase()}`}
                        >
                          {selectedVoucher.status}
                        </span>
                        <span
                          className={`bx-status-badge bx-status-badge--${selectedVoucher.paymentStatus.toLowerCase()}`}
                        >
                          {selectedVoucher.paymentStatus}
                        </span>
                      </div>
                    </div>

                    <div className='section-divider'></div>

                    <div className='voucher-detail-item'>
                      <label className='voucher-detail-label'>System Information</label>
                      <p className='voucher-detail-value'>
                        <small className='text-muted'>Resource ID:</small>
                        <br />
                        <code>{selectedVoucher.resourceId}</code>
                      </p>
                    </div>
                  </div>

                  {/* Timeline Section */}
                  <div className='voucher-detail-section'>
                    <h5 className='mb-3 text-dark'>Timeline</h5>

                    <div className='voucher-detail-item'>
                      <label className='voucher-detail-label'>Created</label>
                      <p className='voucher-detail-value'>
                        <i className='bi bi-calendar-plus me-2'></i>
                        {new Date(selectedVoucher.createdAt).toLocaleString()}
                      </p>
                    </div>

                    <div className='voucher-detail-item'>
                      <label className='voucher-detail-label'>Expires</label>
                      <p className='voucher-detail-value'>
                        <i className='bi bi-calendar-x me-2'></i>
                        {new Date(selectedVoucher.expiresAt).toLocaleString()}
                      </p>
                    </div>

                    {selectedVoucher.usedAt && (
                      <div className='voucher-detail-item'>
                        <label className='voucher-detail-label'>Used</label>
                        <p className='voucher-detail-value text-success'>
                          <i className='bi bi-check-circle me-2'></i>
                          {new Date(selectedVoucher.usedAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className='col-md-6'>
                  {/* Owner Details Section */}
                  <div className='user-details-card'>
                    <h5 className='mb-3'>
                      <i className='bi bi-person-circle me-2'></i>
                      Owner Information
                    </h5>

                    <div className='voucher-detail-item'>
                      <label className='voucher-detail-label'>Name</label>
                      <p className='voucher-detail-value'>
                        {selectedVoucher.user.firstName} {selectedVoucher.user.lastName}
                      </p>
                    </div>

                    <div className='contact-info'>
                      <small>
                        <i className='bi bi-envelope me-2'></i>
                        {selectedVoucher.user.email}
                        <br />
                        <i className='bi bi-phone me-2'></i>
                        {selectedVoucher.user.phone}
                        <br />
                        <i className='bi bi-fingerprint me-2'></i>
                        ID: {selectedVoucher.user.resourceId}
                      </small>
                    </div>

                    <div className='voucher-detail-item mt-3'>
                      <label className='voucher-detail-label'>Verification Status</label>
                      <div className='verification-status'>
                        <small>
                          <i
                            className={`bi bi-envelope${selectedVoucher.user.emailVerifiedAt ? '-check' : '-x'} me-2`}
                          ></i>
                          Email:{' '}
                          {selectedVoucher.user.emailVerifiedAt
                            ? `Verified (${new Date(selectedVoucher.user.emailVerifiedAt).toLocaleDateString()})`
                            : 'Not verified'}
                          <br />
                          <i
                            className={`bi bi-phone${selectedVoucher.user.phoneVerifiedAt ? '-check' : '-x'} me-2`}
                          ></i>
                          Phone:{' '}
                          {selectedVoucher.user.phoneVerifiedAt
                            ? `Verified (${new Date(selectedVoucher.user.phoneVerifiedAt).toLocaleDateString()})`
                            : 'Not verified'}
                        </small>
                      </div>
                    </div>

                    {selectedVoucher.user.disabledAt && (
                      <div className='voucher-detail-item'>
                        <label className='voucher-detail-label'>Account Status</label>
                        <p className='voucher-detail-value text-danger'>
                          <i className='bi bi-exclamation-triangle me-2'></i>
                          Disabled: {new Date(selectedVoucher.user.disabledAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Recipient Details Section */}
                  {selectedVoucher.recipientUser && (
                    <div className='voucher-detail-section'>
                      <h5 className='mb-3 text-success'>
                        <i className='bi bi-person-badge me-2'></i>
                        Recipient Information
                      </h5>

                      <div className='voucher-detail-item'>
                        <label className='voucher-detail-label'>Name</label>
                        <p className='voucher-detail-value'>
                          {selectedVoucher.recipientUser.firstName}{' '}
                          {selectedVoucher.recipientUser.lastName}
                        </p>
                      </div>

                      <div className='contact-info'>
                        <small>
                          <i className='bi bi-envelope me-2'></i>
                          {selectedVoucher.recipientUser.email}
                          <br />
                          <i className='bi bi-phone me-2'></i>
                          {selectedVoucher.recipientUser.phone}
                          <br />
                          <i className='bi bi-fingerprint me-2'></i>
                          ID: {selectedVoucher.recipientUser.resourceId}
                        </small>
                      </div>

                      <div className='voucher-detail-item mt-3'>
                        <label className='voucher-detail-label'>Verification Status</label>
                        <div className='verification-status'>
                          <small>
                            <i
                              className={`bi bi-envelope${selectedVoucher.recipientUser.emailVerifiedAt ? '-check' : '-x'} me-2`}
                            ></i>
                            Email:{' '}
                            {selectedVoucher.recipientUser.emailVerifiedAt
                              ? `Verified (${new Date(selectedVoucher.recipientUser.emailVerifiedAt).toLocaleDateString()})`
                              : 'Not verified'}
                            <br />
                            <i
                              className={`bi bi-phone${selectedVoucher.recipientUser.phoneVerifiedAt ? '-check' : '-x'} me-2`}
                            ></i>
                            Phone:{' '}
                            {selectedVoucher.recipientUser.phoneVerifiedAt
                              ? `Verified (${new Date(selectedVoucher.recipientUser.phoneVerifiedAt).toLocaleDateString()})`
                              : 'Not verified'}
                          </small>
                        </div>
                      </div>

                      {selectedVoucher.recipientUser.disabledAt && (
                        <div className='voucher-detail-item'>
                          <label className='voucher-detail-label'>Account Status</label>
                          <p className='voucher-detail-value text-danger'>
                            <i className='bi bi-exclamation-triangle me-2'></i>
                            Disabled:{' '}
                            {new Date(selectedVoucher.recipientUser.disabledAt).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </ModalWrapper>
    </div>
  )
}

export default VouchersList
